/**
 * @typedef {object} AnnouncementLinkInput
 * @property {string} [label]
 * @property {string} [url]
 */

/**
 * @typedef {object} AnnouncementAttachmentInput
 * @property {string} [filename]
 * @property {string} [url]
 * @property {string} [mimeType]
 */

/**
 * @typedef {object} AnnouncementCourseRef
 * @property {string} [_id]
 */

/**
 * @typedef {object} AnnouncementDraftSource
 * @property {string} [title]
 * @property {string} [displayName]
 * @property {string} [content]
 * @property {string} [scope]
 * @property {string | AnnouncementCourseRef | null} [courseId]
 * @property {AnnouncementLinkInput[]} [links]
 * @property {AnnouncementAttachmentInput[]} [attachments]
 */

/**
 * @typedef {object} AnnouncementDraftForm
 * @property {string} title
 * @property {string} displayName
 * @property {string} content
 * @property {"global" | "course"} scope
 * @property {string} courseId
 * @property {Array<{ label: string, url: string }>} links
 * @property {Array<{ filename: string, url: string, mimeType: string }>} attachments
 */

const EMPTY_LINK = { label: "", url: "" };
const EMPTY_ATTACHMENT = { filename: "", url: "", mimeType: "" };

/**
 * @param {string | AnnouncementCourseRef | null | undefined} courseId
 * @returns {string}
 */
function resolveCourseId(courseId) {
  if (courseId == null) {
    return "";
  }
  if (typeof courseId === "string") {
    return courseId;
  }
  if (typeof courseId === "object" && courseId._id != null) {
    return String(courseId._id);
  }
  return "";
}

/**
 * Maps a stored draft into the staff announcement form without duplicating empty rows.
 *
 * @param {AnnouncementDraftSource | null | undefined} announcement
 * @returns {AnnouncementDraftForm}
 */
export function announcementToDraftForm(announcement) {
  const rawLinks = Array.isArray(announcement?.links) ? announcement.links : [];
  const rawAttachments = Array.isArray(announcement?.attachments)
    ? announcement.attachments
    : [];

  const links =
    rawLinks.length > 0
      ? rawLinks.map((link) => ({
          label: typeof link?.label === "string" ? link.label : "",
          url: typeof link?.url === "string" ? link.url : "",
        }))
      : [{ ...EMPTY_LINK }];

  const attachments =
    rawAttachments.length > 0
      ? rawAttachments.map((file) => ({
          filename: typeof file?.filename === "string" ? file.filename : "",
          url: typeof file?.url === "string" ? file.url : "",
          mimeType: typeof file?.mimeType === "string" ? file.mimeType : "",
        }))
      : [{ ...EMPTY_ATTACHMENT }];

  return {
    title: typeof announcement?.title === "string" ? announcement.title : "",
    displayName: typeof announcement?.displayName === "string" ? announcement.displayName : "",
    content: typeof announcement?.content === "string" ? announcement.content : "",
    scope: announcement?.scope === "course" ? "course" : "global",
    courseId: resolveCourseId(announcement?.courseId),
    links,
    attachments,
  };
}
